import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';
import { Search } from 'lucide-react';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  category_id: number | null;
  subcategory_id: number | null;
  is_transfer: boolean;
  is_refund?: boolean;
}

interface Category {
  id: number;
  name: string;
  color: string;
  subcategories: { id: number; name: string }[];
}

interface CategoryOption {
  displayText: string;
  categoryId: number;
  subcategoryId: number | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onConfirm: (categoryId: number, subcategoryId: number | null) => void;
  categories: Category[];
}

const CONTROL_BG = 'bg-[var(--background-dark)]';
const TRIGGER_CLASS = cn(
  'flex h-11 w-full items-center justify-between rounded-lg border border-border px-3 py-2.5 text-[15px] font-normal',
  'text-foreground/70 ring-offset-background',
  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  CONTROL_BG
);
const CONTENT_CLASS = cn(
  'relative z-50 max-h-[300px] overflow-hidden rounded-lg border border-border text-foreground/90 shadow-md',
  CONTROL_BG
);
const ITEM_CLASS = cn(
  'relative flex w-full cursor-pointer select-none items-center rounded-md py-2.5 pl-3 pr-4 text-sm font-normal outline-none text-left',
  'hover:bg-[var(--background-card)]'
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const SimpleRefundModal: React.FC<Props> = ({
  isOpen,
  onClose,
  transaction,
  onConfirm,
  categories,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
      setPickerOpen(false);
      setSearchTerm('');
    }
  }, [isOpen]);

  const options: CategoryOption[] = useMemo(() => {
    const result: CategoryOption[] = [];
    categories.forEach((category) => {
      if (category.subcategories && category.subcategories.length > 0) {
        category.subcategories.forEach((sub) => {
          result.push({
            displayText: `${category.name} - ${sub.name}`,
            categoryId: category.id,
            subcategoryId: sub.id,
          });
        });
      } else {
        result.push({
          displayText: category.name,
          categoryId: category.id,
          subcategoryId: null,
        });
      }
    });
    return result;
  }, [categories]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const lower = searchTerm.toLowerCase();
    return options.filter((opt) => opt.displayText.toLowerCase().includes(lower));
  }, [options, searchTerm]);

  const selectedOption = options.find(
    (opt) =>
      opt.categoryId === selectedCategoryId &&
      (opt.subcategoryId ?? null) === (selectedSubcategoryId ?? null)
  );

  const handleSelectOption = (opt: CategoryOption) => {
    setSelectedCategoryId(opt.categoryId);
    setSelectedSubcategoryId(opt.subcategoryId);
    setPickerOpen(false);
    setSearchTerm('');
  };

  const handlePickerOpenChange = (open: boolean) => {
    setPickerOpen(open);
    if (!open) setSearchTerm('');
  };

  const handleConfirm = () => {
    if (selectedCategoryId === null) return;
    onConfirm(selectedCategoryId, selectedSubcategoryId);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark as Refund</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Select the expense category this refund belongs to.
          </p>

          <Popover open={pickerOpen} onOpenChange={handlePickerOpenChange} modal={false}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={TRIGGER_CLASS}
                aria-expanded={pickerOpen}
                aria-haspopup="listbox"
              >
                <span className="line-clamp-1 text-left flex-1">
                  {selectedOption ? (
                    selectedOption.displayText
                  ) : (
                    <span className="text-muted-foreground/80">Select a category...</span>
                  )}
                </span>
                <span className="inline-flex h-4 w-4 shrink-0 opacity-50">
                  <ChevronDownIcon />
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              disablePortal
              sideOffset={0}
              className={cn('w-[var(--radix-popover-trigger-width)] !p-0', CONTENT_CLASS)}
              align="start"
              style={{ pointerEvents: 'auto' }}
            >
              <div className="flex flex-col">
                <div className="flex items-center border-b border-border px-3 gap-2">
                  <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={cn(
                      'flex h-11 w-full rounded-md bg-transparent pt-2 pb-0.5 text-sm outline-none placeholder:text-muted-foreground/80',
                      CONTROL_BG
                    )}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
                <div
                  role="listbox"
                  className="max-h-[240px] overflow-y-auto overflow-x-hidden px-2 pt-0.5 pb-2"
                >
                  {filteredOptions.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No categories found
                    </div>
                  ) : (
                    filteredOptions.map((opt) => (
                      <div
                        key={`${opt.categoryId}-${opt.subcategoryId}`}
                        role="option"
                        tabIndex={-1}
                        className={ITEM_CLASS}
                        onClick={() => handleSelectOption(opt)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectOption(opt);
                          }
                        }}
                      >
                        {opt.displayText}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={selectedCategoryId === null || selectedCategoryId === undefined}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
